import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { SituationPage } from "@/components/situation-page";
import { FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "What are special conditions in a Queensland contract?",
    a: "They're extra terms added to the standard contract for your particular deal. Common ones are finance, building and pest, subject to the sale of your home, and due diligence. They sit on top of the standard terms and can change how the whole contract works.",
  },
  {
    q: "What's a finance condition?",
    a: "It makes the contract depend on you getting loan approval by a set date. If finance isn't approved, you can end the contract, but only if you act properly before the deadline. Missing the date can leave you bound without a loan.",
  },
  {
    q: "What's a building and pest condition?",
    a: "It lets you get an inspection by a set date and end the contract if the report isn't satisfactory. Keep the report and act before the date. If you want to negotiate a price reduction instead, get advice first.",
  },
  {
    q: "Can I buy subject to selling my own home?",
    a: "Yes, with a 'subject to sale' condition. Sellers often add a clause letting them keep marketing the property and give you a short time to drop the condition if another buyer comes along. Know how that clause works before you sign.",
  },
  {
    q: "What special conditions should I be wary of?",
    a: "Short finance or inspection dates, clauses that make you responsible for defects, early release of the deposit, extra fees or penalties for late settlement, and anything written by hand that you don't fully understand. Have them reviewed before you sign.",
  },
  {
    q: "Can special conditions be changed after signing?",
    a: "Only if both sides agree in writing. That's why it's far easier to get them right before you sign.",
  },
];

export const Route = createFileRoute("/special-conditions-contract-qld")({
  head: () => ({
    meta: [
      { title: fitTitle("Special Conditions in a QLD Contract of Sale | Coastside Law") },
      {
        name: "description",
        content: fitDescription(
          "Common special conditions in Queensland property contracts explained: finance, building and pest, subject to sale, and the clauses to watch before you sign.",
        ),
      },
      { property: "og:title", content: "Special conditions in a Queensland contract, explained" },
      {
        property: "og:description",
        content: fitDescription("Finance, building and pest, subject to sale and the clauses buyers should question."),
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "/special-conditions-contract-qld" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/special-conditions-contract-qld" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: () => (
    <SituationPage
      eyebrow="Buying property"
      title="Special conditions in a Queensland contract"
      lead="The special conditions are where most contract problems hide. Here are the common ones, what they do, and the clauses worth questioning before you sign."
      boxTitle="The ones you'll see most"
      boxItems={[
        "Finance: the contract depends on loan approval by a set date",
        "Building and pest: you can end the contract if the report is unsatisfactory",
        "Subject to sale: depends on selling your own home",
        "Due diligence: time to check things like zoning or a business",
        "Every condition has a deadline, and missing it can leave you bound",
      ]}
      faqs={FAQS}
      links={[
        { to: "/buyers-solicitor-qld", label: "Buyer's solicitor" },
        { to: "/cooling-off-period-qld", label: "Cooling-off period in QLD" },
        { to: "/sunset-date-off-the-plan-qld", label: "Sunset clauses" },
        { to: "/conveyancing", label: "Conveyancing" },
      ]}
      formHeading="Send us your contract before you sign"
      ctaTitle="Not sure about a clause?"
      ctaBody={`Call ${FIRM.phone}. We'll review your contract free before you sign.`}
    />
  ),
});
